import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Copy, Check, Coins, Trophy, Link2, Share2, Flame, Timer, Hourglass, QrCode, Download, Printer } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { QuickShareBar } from '../components/QuickShareBar';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Share links use the live host the player is currently browsing (e.g.
// https://betohalics.com in production, the preview URL in dev) so we never
// hand out a stale build-time URL like slot-game-demo.emergent.host.
const APP_URL = typeof window !== 'undefined'
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL;

const Referrals = () => {
    const { user } = useAuth();
    const [referralData, setReferralData] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [copied, setCopied] = useState(false);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [refRes, lbRes] = await Promise.all([
                axios.get(`${API}/user/referral`, { withCredentials: true }),
                axios.get(`${API}/referral/leaderboard`, { withCredentials: true })
            ]);
            setReferralData(refRes.data);
            setLeaderboard(lbRes.data);
        } catch (err) {
            console.error('Failed to fetch referral data:', err);
        } finally {
            setLoading(false);
        }
    };

    // Share URL goes through the personalised unfurl endpoint so crawlers
    // (iMessage / WhatsApp / Telegram / Discord / Twitter) get a per-referrer
    // OG card with the inviter's name + code. Real users get bounced to
    // /register?ref=XXX so signup attribution is unchanged.
    const referralLink = referralData
        ? `${APP_URL}/api/r/${referralData.referral_code}`
        : '';

    const copyLink = () => {
        navigator.clipboard.writeText(referralLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const copyCode = () => {
        navigator.clipboard.writeText(referralData?.referral_code || '');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#050505]">
                <Header />
                <div className="flex items-center justify-center py-24">
                    <div className="spinner" />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#050505]">
            <Header />
            <div className="max-w-5xl mx-auto px-4 py-8">
                <div className="text-center mb-8">
                    <h1 className="text-3xl sm:text-4xl font-black text-white">
                        Refer & <span className="text-[#39FF14]">Earn</span>
                    </h1>
                    <p className="text-[#A0AAB2] mt-2">Invite friends — earn $10 Free Play each time one loads $20!</p>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-dark p-6 text-center">
                        <Users className="w-8 h-8 text-[#00FFFF] mx-auto mb-2" />
                        <p className="text-3xl font-mono font-bold text-white" data-testid="referral-count">{referralData?.referral_count || 0}</p>
                        <p className="text-[#A0AAB2] text-sm">Friends Referred</p>
                    </motion.div>
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card-dark p-6 text-center">
                        <Coins className="w-8 h-8 text-[#FFD700] mx-auto mb-2" />
                        <p className="text-3xl font-mono font-bold text-[#FFD700]" data-testid="referral-earnings">{(referralData?.referral_earnings || 0).toFixed(2)}</p>
                        <p className="text-[#A0AAB2] text-sm">Total Earned</p>
                    </motion.div>
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="card-dark p-6 text-center">
                        <Share2 className="w-8 h-8 text-[#FF007F] mx-auto mb-2" />
                        {Number(referralData?.referee_bonus || 0) > 0 ? (
                            <>
                                <div className="flex items-center justify-center gap-2">
                                    <p className="text-lg font-mono font-bold text-[#39FF14]">+${referralData?.referrer_bonus || 0}</p>
                                    <span className="text-[#A0AAB2] text-sm">/</span>
                                    <p className="text-lg font-mono font-bold text-[#00FFFF]">+${referralData?.referee_bonus || 0}</p>
                                </div>
                                <p className="text-[#A0AAB2] text-sm">You / Friend earn</p>
                            </>
                        ) : (
                            <>
                                <p className="text-lg font-mono font-bold text-[#39FF14]">+${referralData?.referrer_bonus || 0} Free Play</p>
                                <p className="text-[#A0AAB2] text-sm">when your friend loads ${referralData?.load_threshold ?? 20}</p>
                            </>
                        )}
                    </motion.div>
                </div>

                {/* Referral Link Card */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                    className="card-dark p-6 mb-8">
                    <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                        <h3 className="text-lg font-bold text-white flex items-center gap-2">
                            <Link2 className="w-5 h-5 text-[#FFD700]" />
                            Your Referral Link
                        </h3>
                        {referralData?.boost_active && referralData?.boost_expires_at && (
                            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FF6B00]/15 border border-[#FF6B00]/40" data-testid="referrals-boost-pill">
                                <Flame className="w-4 h-4 text-[#FF6B00] animate-pulse" />
                                <span className="text-xs font-bold text-[#FF6B00] uppercase tracking-wider">Boost</span>
                                <Timer className="w-3.5 h-3.5 text-[#FFD700]" />
                                <span className="text-xs font-mono text-[#FFD700]">
                                    ends {new Date(referralData.boost_expires_at).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} CT
                                </span>
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="flex-1 bg-[#0a0a0a] rounded-lg px-4 py-3 font-mono text-sm text-[#A0AAB2] border border-white/10 overflow-x-auto" data-testid="referral-link">
                            {referralLink}
                        </div>
                        <Button onClick={copyLink} className="btn-primary shrink-0" data-testid="copy-link-btn">
                            {copied ? <><Check className="w-4 h-4 mr-2" />Copied!</> : <><Copy className="w-4 h-4 mr-2" />Copy Link</>}
                        </Button>
                    </div>
                    <div className="mt-4 flex items-center gap-3">
                        <span className="text-[#A0AAB2] text-sm">Your code:</span>
                        <span className="font-mono font-bold text-[#FFD700] text-lg" data-testid="referral-code">{referralData?.referral_code}</span>
                        <button onClick={copyCode} className="text-[#A0AAB2] hover:text-white transition-colors">
                            <Copy className="w-4 h-4" />
                        </button>
                    </div>

                    {referralData?.referral_code && (
                        <div className="mt-5 flex flex-col sm:flex-row items-center gap-4 p-4 bg-[#0a0a0a] rounded-xl border border-[#FFD700]/20"
                             data-testid="referral-qr-card">
                            <img
                                src={`${API}/r/${referralData.referral_code}/qr.png`}
                                alt={`Scan to join with code ${referralData.referral_code}`}
                                className="w-44 h-auto rounded-md shadow-lg bg-white"
                                data-testid="referral-qr-image"
                            />
                            <div className="flex-1 text-center sm:text-left">
                                <div className="flex items-center gap-2 mb-1 justify-center sm:justify-start">
                                    <QrCode className="w-4 h-4 text-[#FFD700]" />
                                    <span className="text-white font-bold text-sm">Your scannable invite</span>
                                </div>
                                <p className="text-xs text-[#A0AAB2] mb-3 max-w-xs">
                                    Show this QR to anyone — when they scan it, they go straight to sign-up with your code pre-applied. Save it to your phone or print it for your shop.
                                </p>
                                <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                                    <a
                                        href={`${API}/r/${referralData.referral_code}/qr.png`}
                                        download={`betohalics-${referralData.referral_code}.png`}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#FFD700] hover:bg-[#FFD700]/90 text-black text-xs font-bold transition-colors"
                                        data-testid="referral-qr-download"
                                    >
                                        <Download className="w-3.5 h-3.5" />
                                        Download QR
                                    </a>
                                    <a
                                        href={`${API}/r/${referralData.referral_code}/flyer.pdf`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-transparent border border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/10 text-xs font-bold transition-colors"
                                        data-testid="referral-flyer-print"
                                        title="Open a print-ready PDF flyer with your QR + tear-off code strips"
                                    >
                                        <Printer className="w-3.5 h-3.5" />
                                        Print flyer
                                    </a>
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            try {
                                                const blob = await fetch(`${API}/r/${referralData.referral_code}/qr.png`).then(r => r.blob());
                                                if (navigator.share && navigator.canShare?.({ files: [new File([blob], 'qr.png', { type: 'image/png' })] })) {
                                                    await navigator.share({
                                                        files: [new File([blob], `betohalics-${referralData.referral_code}.png`, { type: 'image/png' })],
                                                        title: 'Join me on BETohalics',
                                                        text: `Scan to sign up with my code ${referralData.referral_code}`,
                                                    });
                                                } else {
                                                    // Fallback — open in a new tab so the user can save manually
                                                    window.open(`${API}/r/${referralData.referral_code}/qr.png`, '_blank');
                                                }
                                            } catch { /* user dismissed */ }
                                        }}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-transparent border border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/10 text-xs font-bold transition-colors"
                                        data-testid="referral-qr-share"
                                    >
                                        <Share2 className="w-3.5 h-3.5" />
                                        Share QR
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    <QuickShareBar
                        referralLink={referralLink}
                        referrerBonus={referralData?.referrer_bonus}
                        refereeBonus={referralData?.referee_bonus}
                        loadThreshold={referralData?.load_threshold ?? 20}
                        boostActive={referralData?.boost_active}
                    />
                </motion.div>

                {/* Tab Switcher */}
                <div className="flex gap-2 mb-6">
                    {[
                        { key: 'overview', label: 'My Referrals' },
                        { key: 'leaderboard', label: 'Referral Leaderboard' }
                    ].map(tab => (
                        <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                            className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                                activeTab === tab.key ? 'bg-[#FFD700] text-black' : 'bg-white/5 text-[#A0AAB2] hover:bg-white/10'
                            }`}
                            data-testid={`tab-${tab.key}`}>
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* My Referrals Tab */}
                {activeTab === 'overview' && (
                    <div className="space-y-4">
                        {/* Pending referrals — visible nudge for almost-there friends */}
                        {(referralData?.pending_referrals || []).length > 0 && (
                            <div className="card-dark p-6" data-testid="pending-referrals-card">
                                <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
                                    <Hourglass className="w-5 h-5 text-[#FF6B00]" />
                                    Almost there ({referralData.pending_referrals.length})
                                </h3>
                                <p className="text-xs text-[#A0AAB2] mb-4">
                                    These friends signed up. They unlock your bonus once they load $
                                    {referralData?.load_threshold ?? 20}.
                                </p>
                                <div className="space-y-3">
                                    {referralData.pending_referrals.map((r, i) => {
                                        const threshold = r.load_threshold || referralData.load_threshold || 20;
                                        const progress = r.loaded_progress || 0;
                                        const pct = Math.min(100, threshold > 0 ? (progress / threshold) * 100 : 0);
                                        return (
                                            <div key={i} className="bg-[#0a0a0a] rounded-lg p-3 border border-white/5" data-testid={`pending-referral-${i}`}>
                                                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                                                    <span className="text-white font-bold">{r.referee_name}</span>
                                                    <span className="text-[#A0AAB2] text-xs font-mono tabular-nums">
                                                        ${progress.toFixed(0)} / ${threshold.toFixed(0)} loaded
                                                    </span>
                                                </div>
                                                <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full bg-gradient-to-r from-[#FF6B00] to-[#39FF14] transition-all"
                                                        style={{ width: `${pct}%` }}
                                                    />
                                                </div>
                                                <p className="text-xs text-[#A0AAB2] mt-2">
                                                    {r.remaining > 0 ? (
                                                        <>Needs <span className="text-[#FFD700] font-bold">${r.remaining.toFixed(0)}</span> more — unlocks <span className="text-[#39FF14] font-bold">+${(r.referrer_bonus || 0).toFixed(0)}</span> for you</>
                                                    ) : (
                                                        <span className="text-[#39FF14]">Payout pending — refresh soon</span>
                                                    )}
                                                </p>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div className="card-dark p-6">
                        {(!referralData?.referrals || referralData.referrals.length === 0) ? (
                            <div className="text-center py-12">
                                <Users className="w-12 h-12 text-[#A0AAB2]/30 mx-auto mb-3" />
                                <p className="text-[#A0AAB2]">No completed referrals yet. Share your link to start earning!</p>
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-white/10">
                                        <TableHead className="text-[#A0AAB2]">Friend</TableHead>
                                        <TableHead className="text-[#A0AAB2] text-right">Your Bonus</TableHead>
                                        <TableHead className="text-[#A0AAB2] text-right">Their Bonus</TableHead>
                                        <TableHead className="text-[#A0AAB2]">Date</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {referralData.referrals.map((ref, i) => (
                                        <TableRow key={i} className="border-white/10" data-testid={`referral-row-${i}`}>
                                            <TableCell className="text-white font-medium">{ref.referee_name}</TableCell>
                                            <TableCell className="text-right font-mono text-[#39FF14]">+{ref.referrer_bonus}</TableCell>
                                            <TableCell className="text-right font-mono text-[#00FFFF]">+{ref.referee_bonus}</TableCell>
                                            <TableCell className="text-[#A0AAB2] text-sm">
                                                {ref.created_at ? new Date(ref.created_at).toLocaleDateString('en-US', { timeZone: 'America/Chicago' }) : ''}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                        </div>
                    </div>
                )}

                {/* Leaderboard Tab */}
                {activeTab === 'leaderboard' && (
                    <div className="card-dark p-6">
                        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                            <Trophy className="w-5 h-5 text-[#FFD700]" />
                            Top Referrers
                        </h3>
                        {leaderboard.length === 0 ? (
                            <p className="text-center text-[#A0AAB2] py-8">No referrals yet. Be the first!</p>
                        ) : (
                            <div className="space-y-2">
                                {leaderboard.map((entry, i) => (
                                    <div key={i} className={`flex items-center justify-between p-4 rounded-lg ${
                                        i === 0 ? 'bg-[#FFD700]/10 border border-[#FFD700]/30' :
                                        i === 1 ? 'bg-[#C0C0C0]/10 border border-[#C0C0C0]/20' :
                                        i === 2 ? 'bg-[#CD7F32]/10 border border-[#CD7F32]/20' :
                                        'bg-white/5'
                                    }`} data-testid={`leaderboard-row-${i}`}>
                                        <div className="flex items-center gap-4">
                                            <span className={`text-2xl font-black w-8 text-center ${
                                                i === 0 ? 'text-[#FFD700]' : i === 1 ? 'text-[#C0C0C0]' : i === 2 ? 'text-[#CD7F32]' : 'text-[#A0AAB2]'
                                            }`}>
                                                {entry.rank}
                                            </span>
                                            <div>
                                                <p className="text-white font-bold">{entry.name}</p>
                                                <p className="text-xs text-[#A0AAB2]">{entry.vip_name}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-white font-mono font-bold">{entry.referral_count} referrals</p>
                                            <p className="text-[#FFD700] font-mono text-sm">{(entry.referral_earnings || 0).toFixed(2)} earned</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* How it Works */}
                <div className="mt-8 card-dark p-6">
                    <h3 className="text-lg font-bold text-white mb-4">How It Works</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                        {[
                            { step: '1', title: 'Share Your Link', desc: 'Copy your unique referral link and share it with friends' },
                            { step: '2', title: 'Friend Signs Up', desc: 'Your friend creates an account using your referral link' },
                            { step: '3', title: 'You Earn Free Play', desc: `Once your friend loads $${referralData?.load_threshold ?? 20}, you get $${referralData?.referrer_bonus ?? 10} Free Play` }
                        ].map(item => (
                            <div key={item.step} className="text-center">
                                <div className="w-10 h-10 rounded-full bg-[#FFD700]/20 text-[#FFD700] font-bold text-lg flex items-center justify-center mx-auto mb-3">
                                    {item.step}
                                </div>
                                <p className="text-white font-medium">{item.title}</p>
                                <p className="text-[#A0AAB2] text-sm mt-1">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Referrals;
